import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { useApi, type ApiClient } from './api-client.js';
import { useRealtime, useRealtimeMessages, type RealtimeEvent } from './realtime.js';

/**
 * 在席状態（03 §11.3a）のブラウザ側。
 *  - 画面は usePresence(userIds) を呼ぶだけ。要求はまとめて 1 本の GET /presence?userIds= にし、結果を共有キャッシュに持つ
 *  - realtime の presence チャネル（presence.changed）が来たらキャッシュを差し替える。接続が無い間は 60 秒ごとに取り直す
 *  - 形は core の PresenceSummary（@saas/module-core/api）と同じ。日時は JSON なので文字列
 */
export type PresenceState = 'online' | 'away' | 'dnd' | 'offline';
export type PresenceActivity = 'on_call' | 'in_meeting' | 'in_chat' | 'idle';

export interface PresenceInfo {
  userId: string;
  state: PresenceState;
  activity: PresenceActivity | null;
  activitySrc: string | null;
  activityRef: string | null;
  manual: boolean;
  since: string;
  autoUntil: string | null;
  device: string | null;
  manualState?: PresenceState | null;
  manualUntil?: string | null;
}

export const PRESENCE_LABEL: Record<PresenceState, string> = { online: '在席', away: '離席', dnd: '取り込み中', offline: '離線' };
export const ACTIVITY_LABEL: Record<PresenceActivity, string> = { on_call: '通話中', in_meeting: '会議中', in_chat: 'チャット中', idle: '離席' };

const STALE_MS = 60_000;
const BATCH_DELAY_MS = 30;
const MAX_IDS_PER_REQUEST = 200;

type Listener = () => void;

/** 画面をまたいで共有するキャッシュ（モジュール内に 1 つ） */
class PresenceStore {
  private readonly entries = new Map<string, PresenceInfo>();
  private readonly fetchedAt = new Map<string, number>();
  private readonly listeners = new Set<Listener>();
  private readonly queue = new Set<string>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private api: ApiClient | null = null;
  private snapshot: ReadonlyMap<string, PresenceInfo> = new Map();

  subscribe = (l: Listener): (() => void) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };

  getSnapshot = (): ReadonlyMap<string, PresenceInfo> => this.snapshot;

  /** 未取得か古いものだけをまとめて取りに行く */
  ensure(api: ApiClient, userIds: readonly string[], force = false): void {
    this.api = api;
    const now = Date.now();
    for (const id of userIds) {
      const at = this.fetchedAt.get(id);
      if (force || at === undefined || now - at > STALE_MS) this.queue.add(id);
    }
    if (this.queue.size === 0 || this.timer) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.flush();
    }, BATCH_DELAY_MS);
  }

  apply(info: PresenceInfo): void {
    this.entries.set(info.userId, info);
    this.fetchedAt.set(info.userId, Date.now());
    this.publish();
  }

  /** 全部古い扱いにする（再接続の差分が取り切れなかったとき） */
  invalidateAll(): void {
    this.fetchedAt.clear();
  }

  private async flush(): Promise<void> {
    const api = this.api;
    if (!api) return;
    const ids = [...this.queue].slice(0, MAX_IDS_PER_REQUEST);
    for (const id of ids) {
      this.queue.delete(id);
      this.fetchedAt.set(id, Date.now()); // 取得中の二重要求を防ぐ
    }
    try {
      const rows = await api.get<PresenceInfo[]>('/presence', { userIds: ids.join(',') });
      for (const r of rows) this.entries.set(r.userId, r);
      this.publish();
    } catch {
      // 権限なし・到達不能: 未知のまま（PresenceDot は出さない）。次の stale で再試行
      for (const id of ids) this.fetchedAt.set(id, Date.now() - STALE_MS + 10_000);
    }
    if (this.queue.size > 0) this.ensure(api, [...this.queue], true);
  }

  private publish(): void {
    this.snapshot = new Map(this.entries);
    for (const l of this.listeners) l();
  }
}

const store = new PresenceStore();

/** テスト・ログアウト時にキャッシュを空にする */
export function resetPresenceCache(): void {
  store.invalidateAll();
}

/**
 * 指定ユーザーの在席状態。未取得の間は undefined（点を出さない）。
 * 呼ぶたびに GET を投げない（30ms でまとめる・60 秒はキャッシュ・realtime の presence.changed で更新）
 */
export function usePresence(userIds: readonly string[]): Record<string, PresenceInfo | undefined> {
  const api = useApi();
  const bus = useRealtime();
  const key = useMemo(() => [...new Set(userIds)].sort().join(','), [userIds]);
  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);

  useEffect(() => {
    const ids = key ? key.split(',') : [];
    if (ids.length === 0) return;
    store.ensure(api, ids);
    // realtime が無い（接続していない）間は 60 秒ごとのポーリングで追いつく
    if (bus.status === 'open') return;
    const t = setInterval(() => store.ensure(api, ids), STALE_MS);
    return () => clearInterval(t);
  }, [api, key, bus.status]);

  useRealtimeMessages(['presence'], (e: RealtimeEvent) => {
    if (e.type !== 'presence.changed') return;
    const p = e.payload as Partial<PresenceInfo> | null;
    if (p && typeof p.userId === 'string' && typeof p.state === 'string') store.apply(p as PresenceInfo);
  });

  return useMemo(() => {
    const out: Record<string, PresenceInfo | undefined> = {};
    for (const id of key ? key.split(',') : []) out[id] = snapshot.get(id);
    return out;
  }, [key, snapshot]);
}

/** 1 人分（アバターの点など） */
export function useUserPresence(userId: string | null | undefined): PresenceInfo | undefined {
  const ids = useMemo(() => (userId ? [userId] : []), [userId]);
  const map = usePresence(ids);
  return userId ? map[userId] : undefined;
}

/** presence.changed を受けてキャッシュに反映する（自分の状態を変えた直後など、画面が即時に反映したいとき） */
export function applyPresence(info: PresenceInfo): void {
  store.apply(info);
}
